'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import CopyButton from '@/components/tools/CopyButton';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type Style = 'classic' | 'modern' | 'minimal';

interface FormData {
  fullName: string;
  jobTitle: string;
  company: string;
  phone: string;
  email: string;
  website: string;
  linkedinUrl: string;
  twitterHandle: string;
  style: Style;
  accentColor: string;
}

/* ------------------------------------------------------------------ */
/*  Color presets                                                      */
/* ------------------------------------------------------------------ */

const COLOR_SWATCHES = [
  { name: 'Blue', value: '#2563eb' },
  { name: 'Green', value: '#16a34a' },
  { name: 'Purple', value: '#7c3aed' },
  { name: 'Red', value: '#dc2626' },
  { name: 'Orange', value: '#ea580c' },
  { name: 'Gray', value: '#4b5563' },
];

/* ------------------------------------------------------------------ */
/*  HTML generators                                                    */
/* ------------------------------------------------------------------ */

function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Allow only http(s)/mailto schemes; reject javascript:, data:, etc.
function safeHref(value: string): string {
  if (!value) return '';
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return '';
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:' && url.protocol !== 'mailto:') {
    return '';
  }
  return esc(url.toString());
}

// Hex color allowlist for accentColor (used inside style="color:..."). Anything
// that isn't #abc / #abcdef gets the default blue.
function safeColor(value: string): string {
  return /^#[0-9a-fA-F]{3,8}$/.test(value) ? value : '#2563eb';
}

function generateClassicHTML(data: FormData): string {
  const { fullName, jobTitle, company, phone, email, website, linkedinUrl, twitterHandle } = data;
  const accentColor = safeColor(data.accentColor);

  const socialLinks: string[] = [];
  if (linkedinUrl) {
    const href = safeHref(linkedinUrl);
    if (href) {
      socialLinks.push(
        `<a href="${href}" style="color:${accentColor};text-decoration:none;font-size:13px;" target="_blank">LinkedIn</a>`
      );
    }
  }
  if (twitterHandle) {
    const handle = twitterHandle.startsWith('@') ? twitterHandle : `@${twitterHandle}`;
    const url = `https://x.com/${encodeURIComponent(handle.replace('@', ''))}`;
    socialLinks.push(
      `<a href="${esc(url)}" style="color:${accentColor};text-decoration:none;font-size:13px;" target="_blank">Twitter/X</a>`
    );
  }
  if (website) {
    const raw = website.startsWith('http') ? website : `https://${website}`;
    const href = safeHref(raw);
    if (href) {
      socialLinks.push(
        `<a href="${href}" style="color:${accentColor};text-decoration:none;font-size:13px;" target="_blank">${esc(website.replace(/^https?:\/\//, ''))}</a>`
      );
    }
  }

  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:#333333;">
  <tr>
    <td style="padding:0 0 8px 0;">
      <strong style="font-size:16px;color:#111111;">${esc(fullName)}</strong>
    </td>
  </tr>${jobTitle || company ? `
  <tr>
    <td style="padding:0 0 8px 0;font-size:13px;color:#555555;">
      ${esc(jobTitle)}${jobTitle && company ? ' | ' : ''}${esc(company)}
    </td>
  </tr>` : ''}
  <tr>
    <td style="padding:0 0 8px 0;">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td style="width:100%;height:1px;background-color:${accentColor};font-size:1px;line-height:1px;" colspan="1">&nbsp;</td>
        </tr>
      </table>
    </td>
  </tr>${phone ? `
  <tr>
    <td style="padding:0 0 4px 0;font-size:13px;color:#555555;">
      ${esc(phone)}
    </td>
  </tr>` : ''}${email ? `
  <tr>
    <td style="padding:0 0 4px 0;font-size:13px;">
      <a href="mailto:${esc(email)}" style="color:${accentColor};text-decoration:none;">${esc(email)}</a>
    </td>
  </tr>` : ''}${socialLinks.length > 0 ? `
  <tr>
    <td style="padding:4px 0 0 0;">
      ${socialLinks.join(' &nbsp;|&nbsp; ')}
    </td>
  </tr>` : ''}
</table>`;
}

function generateModernHTML(data: FormData): string {
  const { fullName, jobTitle, company, phone, email, website, linkedinUrl, twitterHandle } = data;
  const accentColor = safeColor(data.accentColor);

  const contactRows: string[] = [];
  if (phone) {
    contactRows.push(
      `<tr><td style="padding:0 0 4px 0;font-size:13px;color:#555555;">${esc(phone)}</td></tr>`
    );
  }
  if (email) {
    contactRows.push(
      `<tr><td style="padding:0 0 4px 0;font-size:13px;"><a href="mailto:${esc(email)}" style="color:${accentColor};text-decoration:none;">${esc(email)}</a></td></tr>`
    );
  }
  if (website) {
    const raw = website.startsWith('http') ? website : `https://${website}`;
    const href = safeHref(raw);
    if (href) {
      contactRows.push(
        `<tr><td style="padding:0 0 4px 0;font-size:13px;"><a href="${href}" style="color:${accentColor};text-decoration:none;">${esc(website.replace(/^https?:\/\//, ''))}</a></td></tr>`
      );
    }
  }
  if (linkedinUrl) {
    const href = safeHref(linkedinUrl);
    if (href) {
      contactRows.push(
        `<tr><td style="padding:0 0 4px 0;font-size:13px;"><a href="${href}" style="color:${accentColor};text-decoration:none;">LinkedIn</a></td></tr>`
      );
    }
  }
  if (twitterHandle) {
    const handle = twitterHandle.startsWith('@') ? twitterHandle : `@${twitterHandle}`;
    const url = `https://x.com/${encodeURIComponent(handle.replace('@', ''))}`;
    contactRows.push(
      `<tr><td style="padding:0 0 4px 0;font-size:13px;"><a href="${esc(url)}" style="color:${accentColor};text-decoration:none;">${esc(handle)}</a></td></tr>`
    );
  }

  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:#333333;">
  <tr>
    <td style="width:4px;background-color:${accentColor};border-radius:2px;" valign="top">&nbsp;</td>
    <td style="padding:0 0 0 16px;" valign="top">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td style="padding:0 0 2px 0;">
            <strong style="font-size:17px;color:#111111;">${esc(fullName)}</strong>
          </td>
        </tr>${jobTitle ? `
        <tr>
          <td style="padding:0 0 2px 0;font-size:13px;color:${accentColor};font-weight:600;">
            ${esc(jobTitle)}
          </td>
        </tr>` : ''}${company ? `
        <tr>
          <td style="padding:0 0 8px 0;font-size:13px;color:#777777;">
            ${esc(company)}
          </td>
        </tr>` : ''}${contactRows.length > 0 ? `
        ${contactRows.join('\n        ')}` : ''}
      </table>
    </td>
  </tr>
</table>`;
}

function generateMinimalHTML(data: FormData): string {
  const { fullName, jobTitle, phone, email } = data;
  const accentColor = safeColor(data.accentColor);

  const contactParts: string[] = [];
  if (email) {
    contactParts.push(
      `<a href="mailto:${esc(email)}" style="color:${accentColor};text-decoration:none;">${esc(email)}</a>`
    );
  }
  if (phone) {
    contactParts.push(`<span>${esc(phone)}</span>`);
  }

  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:#333333;">
  <tr>
    <td style="padding:0 0 2px 0;">
      <strong style="font-size:15px;color:#111111;">${esc(fullName)}</strong>
    </td>
  </tr>${jobTitle ? `
  <tr>
    <td style="padding:0 0 4px 0;font-size:13px;color:#777777;">
      ${esc(jobTitle)}
    </td>
  </tr>` : ''}${contactParts.length > 0 ? `
  <tr>
    <td style="padding:4px 0 0 0;font-size:13px;color:#555555;">
      ${contactParts.join(' &nbsp;&middot;&nbsp; ')}
    </td>
  </tr>` : ''}
</table>`;
}

function generateSignatureHTML(data: FormData): string {
  switch (data.style) {
    case 'modern':
      return generateModernHTML(data);
    case 'minimal':
      return generateMinimalHTML(data);
    case 'classic':
    default:
      return generateClassicHTML(data);
  }
}

function generatePlainText(data: FormData): string {
  const lines: string[] = [];
  lines.push(data.fullName);
  if (data.jobTitle && data.company) {
    lines.push(`${data.jobTitle} | ${data.company}`);
  } else if (data.jobTitle) {
    lines.push(data.jobTitle);
  } else if (data.company) {
    lines.push(data.company);
  }
  lines.push('---');
  if (data.phone) lines.push(data.phone);
  if (data.email) lines.push(data.email);
  if (data.website) lines.push(data.website.replace(/^https?:\/\//, ''));
  if (data.linkedinUrl) lines.push(data.linkedinUrl);
  if (data.twitterHandle) {
    const handle = data.twitterHandle.startsWith('@') ? data.twitterHandle : `@${data.twitterHandle}`;
    lines.push(`Twitter/X: ${handle}`);
  }
  return lines.join('\n');
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function EmailSignatureContent() {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    jobTitle: '',
    company: '',
    phone: '',
    email: '',
    website: '',
    linkedinUrl: '',
    twitterHandle: '',
    style: 'classic',
    accentColor: '#2563eb',
  });

  function updateField(field: keyof FormData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  const signatureHTML = generateSignatureHTML(formData);
  const plainText = generatePlainText(formData);
  const hasContent = formData.fullName.trim().length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Email Signature Generator"
        description="Create a professional email signature with your name, title, contact info, and social links. Copy the HTML and paste it into your email client."
        category="Professional Branding"
      />

      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* -------------------------------------------------------- */}
          {/*  LEFT COLUMN - Input Form                                 */}
          {/* -------------------------------------------------------- */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-5">Your Details</h2>

            <div className="space-y-4">
              {/* Full Name */}
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="fullName"
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => updateField('fullName', e.target.value)}
                  placeholder="Jane Smith"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>

              {/* Job Title */}
              <div>
                <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-1">
                  Job Title
                </label>
                <input
                  id="jobTitle"
                  type="text"
                  value={formData.jobTitle}
                  onChange={(e) => updateField('jobTitle', e.target.value)}
                  placeholder="Senior Product Designer"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>

              {/* Company */}
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                  Company
                </label>
                <input
                  id="company"
                  type="text"
                  value={formData.company}
                  onChange={(e) => updateField('company', e.target.value)}
                  placeholder="Acme Inc."
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  id="phone"
                  type="text"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="jane@acme.com"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>

              {/* Website */}
              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  id="website"
                  type="text"
                  value={formData.website}
                  onChange={(e) => updateField('website', e.target.value)}
                  placeholder="www.janesmith.com"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>

              {/* LinkedIn URL */}
              <div>
                <label htmlFor="linkedinUrl" className="block text-sm font-medium text-gray-700 mb-1">
                  LinkedIn URL
                </label>
                <input
                  id="linkedinUrl"
                  type="text"
                  value={formData.linkedinUrl}
                  onChange={(e) => updateField('linkedinUrl', e.target.value)}
                  placeholder="https://linkedin.com/in/janesmith"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>

              {/* Twitter / X Handle */}
              <div>
                <label htmlFor="twitterHandle" className="block text-sm font-medium text-gray-700 mb-1">
                  Twitter / X Handle
                </label>
                <input
                  id="twitterHandle"
                  type="text"
                  value={formData.twitterHandle}
                  onChange={(e) => updateField('twitterHandle', e.target.value)}
                  placeholder="@janesmith"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>

              {/* Style */}
              <div>
                <label htmlFor="style" className="block text-sm font-medium text-gray-700 mb-1">
                  Style
                </label>
                <select
                  id="style"
                  value={formData.style}
                  onChange={(e) => updateField('style', e.target.value as Style)}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition bg-white"
                >
                  <option value="classic">Classic</option>
                  <option value="modern">Modern</option>
                  <option value="minimal">Minimal</option>
                </select>
              </div>

              {/* Accent Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Accent Color
                </label>
                <div className="flex gap-2">
                  {COLOR_SWATCHES.map((swatch) => (
                    <button
                      key={swatch.value}
                      type="button"
                      title={swatch.name}
                      onClick={() => updateField('accentColor', swatch.value)}
                      className={`w-8 h-8 rounded-full cursor-pointer border-2 transition-all ${
                        formData.accentColor === swatch.value
                          ? 'ring-2 ring-offset-2 border-transparent'
                          : 'border-transparent hover:scale-110'
                      }`}
                      style={{
                        backgroundColor: swatch.value,
                        ...(formData.accentColor === swatch.value
                          ? { boxShadow: `0 0 0 2px white, 0 0 0 4px ${swatch.value}` }
                          : {}),
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* -------------------------------------------------------- */}
          {/*  RIGHT COLUMN - Live Preview                              */}
          {/* -------------------------------------------------------- */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-900">Live Preview</h2>
              <span className="text-xs text-gray-400 uppercase tracking-wide font-semibold">
                {formData.style}
              </span>
            </div>

            {/* Signature preview area */}
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 mb-6 bg-gray-50/50 min-h-[160px]">
              {hasContent ? (
                <div dangerouslySetInnerHTML={{ __html: signatureHTML }} />
              ) : (
                <div className="flex items-center justify-center h-full min-h-[120px]">
                  <p className="text-sm text-gray-400 text-center">
                    Start typing your name to see the preview
                  </p>
                </div>
              )}
            </div>

            {/* Copy buttons */}
            {hasContent && (
              <div className="flex flex-wrap gap-3">
                <CopyButton text={signatureHTML} label="Copy HTML" />
                <CopyButton text={plainText} label="Copy Plain Text" />
              </div>
            )}

            {/* Usage tips */}
            <div className="mt-8 border-t border-gray-100 pt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">How to use your signature</h3>
              <ol className="text-sm text-gray-500 space-y-2 list-decimal list-inside">
                <li>Click <strong className="text-gray-700">Copy HTML</strong> to copy the signature code.</li>
                <li>Open your email client settings (Gmail, Outlook, Apple Mail, etc.).</li>
                <li>Find the signature editor and paste the HTML code.</li>
                <li>Some clients let you paste directly; others need you to switch to the HTML/source view first.</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Educational Section */}
        <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Your Email Signature Matters for Your Career</h3>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Your email signature is one of the most overlooked pieces of professional branding. Every email you send -- whether it is a job application, a client proposal, or an internal message -- ends with your signature. That makes it one of your highest-frequency touchpoints with colleagues, recruiters, and potential employers. A clean, well-structured signature communicates professionalism, attention to detail, and credibility.
            </p>
            <p>
              The best email signatures are simple and functional. Include your <strong>full name</strong>, <strong>job title</strong>, <strong>company</strong>, <strong>phone number</strong>, and <strong>email address</strong> at minimum. Adding a link to your LinkedIn profile or portfolio gives recipients a frictionless way to learn more about your work. Avoid overloading your signature with inspirational quotes, large images, or excessive social media links -- these can trigger spam filters and render poorly across different email clients.
            </p>
            <p>
              From a technical standpoint, HTML-based signatures offer the most consistent formatting across Gmail, Outlook, Apple Mail, and other clients. Use inline CSS styles (not external stylesheets) and table-based layouts for maximum compatibility. Keep your signature under 10 KB in total size, and test it by sending emails to yourself across multiple platforms before rolling it out. A professional signature is a small investment that pays dividends in every interaction.
            </p>
            <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
              <strong>Sources &amp; Further Reading:</strong>{' '}
              <a href="https://www.indeed.com/career-advice/resumes-cover-letters/professional-email-signature" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: Professional Email Signature Examples</a>
              {' · '}
              <a href="https://hbr.org/topic/career-planning" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review: Career Planning</a>
              {' · '}
              <a href="https://www.careeronestop.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">CareerOneStop: Career Resources (U.S. DOL)</a>
            </p>
          </div>
        </section>

        {/* CTA */}
        <ToolCTA
          headline="Make your email sign-off as professional as your resume."
          description="A polished signature is just the start. Build a resume that backs it up."
          buttonText="Build My Resume Free"
        />
      </div>
    </div>
  );
}
